// import components
import { Skeleton } from '@mui/material';
import { StyledRelativeBox } from 'components/shared/Box/Box.styles';
import NoteCard from './NoteCard';
import { StyledIndicatorBox } from './NoteSlider.styles';

// import hooks
import { useEffect, useMemo, useRef, useState } from 'react';

// import types
import { NoteSliderProps } from './NoteSlider.types';

// import utils
import {
  MotionCardSliderAnimation,
  MotionSliderIndicators,
  MotionSliderNavButton
} from './NoteSlider.motion';
import { NOTE_VARIANT } from '../Notes.consts';

export default function NoteSlider({
  notes,
  isFetching,
  onDelete,
  deletingId,
  isEmployeePortal = false,
  noteVariant = NOTE_VARIANT.DEFAULT,
  isCompact = false,
  showTooltip = false
}: NoteSliderProps) {
  const orderedNotes = useMemo(() => [...notes].reverse(), [notes]);
  const [[page, direction], setPage] = useState<[number, number]>([0, 0]);

  const safePage = Math.max(0, Math.min(page, orderedNotes.length - 1));

  // keep state in sync after the paint
  useEffect(() => {
    if (page !== safePage) setPage([safePage, direction]);
  }, [page, safePage, direction]);

  const prevLen = useRef(orderedNotes.length);
  useEffect(() => {
    if (orderedNotes.length > prevLen.current) {
      setPage([0, -1]); // direction = -1 → slide from left
    }
    prevLen.current = orderedNotes.length;
  }, [orderedNotes.length]);

  const paginate = (dir: number) => {
    if (!orderedNotes.length) return;
    setPage(([page]) => [
      (page + dir + orderedNotes.length) % orderedNotes.length,
      dir
    ]);
  };

  if (isFetching) return <Skeleton variant="rectangular" height={160} />;
  if (!orderedNotes.length) return null;

  const current = orderedNotes[safePage];
  const currentId = 'noteId' in current ? current.noteId : current.id;

  return (
    <StyledRelativeBox>
      {/* slider viewport */}
      <MotionCardSliderAnimation currentId={currentId} direction={direction}>
        <NoteCard
          note={current}
          deleting={deletingId === currentId}
          onDelete={() => onDelete(currentId)}
          isEmployeePortal={isEmployeePortal}
          noteVariant={noteVariant}
          isCompact={isCompact}
          showTooltip={showTooltip}
        />
      </MotionCardSliderAnimation>
      {/* nav buttons */}
      {notes.length > 1 && (
        <>
          <MotionSliderNavButton paginate={paginate} position="left" />
          <MotionSliderNavButton paginate={paginate} position="right" />

          {notes.length > 1 && (
            <>
              {/* indicator strip */}
              <StyledIndicatorBox isCompact={isCompact}>
                {notes.map((_, index) => (
                  <MotionSliderIndicators
                    key={index}
                    index={index}
                    setPage={setPage}
                    safePage={safePage}
                  />
                ))}
              </StyledIndicatorBox>
            </>
          )}
        </>
      )}
    </StyledRelativeBox>
  );
}
